import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

import { RECAPTCHA_CHECKBOX_SITEKEY } from '@portal/config/env';

interface RecaptchaCheckboxProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  action?: string;
}

export interface RecaptchaCheckboxHandle {
  reset: () => void;
}

const RecaptchaCheckbox = forwardRef<
  RecaptchaCheckboxHandle,
  RecaptchaCheckboxProps
>(({ onVerify, onExpire, action = 'LOGIN' }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const renderWidget = useCallback(() => {
    const recaptcha = window.grecaptcha?.enterprise;
    if (!recaptcha || !containerRef.current || widgetIdRef.current !== null)
      return;

    recaptcha.ready(() => {
      if (!containerRef.current || widgetIdRef.current !== null) return;

      widgetIdRef.current = recaptcha.render(containerRef.current, {
        sitekey: RECAPTCHA_CHECKBOX_SITEKEY,
        action,
        callback: onVerify,
        'expired-callback': onExpire,
      });
    });
  }, [onVerify, onExpire, action]);

  useImperativeHandle(ref, () => ({
    reset: () => {
      const recaptcha = window.grecaptcha?.enterprise;
      if (!recaptcha || widgetIdRef.current === null) return;
      recaptcha.reset(widgetIdRef.current);
    },
  }));

  useEffect(() => {
    if (!RECAPTCHA_CHECKBOX_SITEKEY) return;

    const interval = setInterval(() => {
      if (window.grecaptcha?.enterprise) {
        clearInterval(interval);
        renderWidget();
      }
    }, 100);

    return () => clearInterval(interval);
  }, [renderWidget]);

  if (!RECAPTCHA_CHECKBOX_SITEKEY) return null;

  return <div ref={containerRef} />;
});

RecaptchaCheckbox.displayName = 'RecaptchaCheckbox';

export default RecaptchaCheckbox;
